import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Code2,
  Shield,
  Zap,
  Brain,
  Clock,
  AlertTriangle,
  Target,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Award,
  Users,
  FileText,
  BarChart3,
  Rocket,
  Zap as Lightning,
  Code,
  Database,
  GitBranch,
  Cpu,
  Eye
} from 'lucide-react';
import '../../styles/ServicePageCommon.css';
import './AIInnovation.css';

const AIInnovation = () => {
  const location = useLocation();
  const services = [
    { title: 'GCC as a Service', path: '/services/gcc', icon: Building2 },
    { title: 'Product Engineering', path: '/services/product-engineering', icon: Code2 },
    { title: 'Infrastructure & Security', path: '/services/infrastructure-security', icon: Shield },
    { title: 'Special Engagements', path: '/services/special-engagements', icon: Zap },
    { title: 'AI & Innovation', path: '/services/ai-innovation', icon: Brain }
  ];

  return (
    <div className="ai-innovation-service-page">
      {/* Main Content */}
      <section className="service-content-section">
        <div className="service-header">
          <h1 className="service-title golden-gradient">AI & Innovation</h1>
          <p className="service-subtitle">
            Turn emerging technology into competitive advantage with practical AI adoption and innovation labs
          </p>
        </div>

        <div className="content-layout">
          {/* Main Content Area */}
          <div className="service-main-content">
            <div className="service-overview">
              <h2>Participate in the AI Revolution</h2>
              <p className="section-intro">
                Artificial Intelligence is transforming every industry. Our comprehensive AI and innovation services
                help you experiment with, adopt, and scale AI technologies to create sustainable competitive advantages.
              </p>
              <p>
                From strategy development to implementation and scaling, we guide you through every step of your AI journey with practical and results-driven approaches.
              </p>
            </div>
            <div className="content-section">
              <h2>Engagement Model</h2>
              <div className="ai-innovation-engagement-models-table">
                <div className="ai-innovation-models-header">
                  <div className="ai-innovation-model-column ai-innovation-empty-header"></div>
                  <div className="ai-innovation-model-column">
                    <h4>Managed AI Labs</h4>
                    <p className="ai-innovation-model-subtitle">Experimental & Prototyping</p>
                  </div>
                  <div className="ai-innovation-model-column">
                    <h4>Strategic</h4>
                    <p className="ai-innovation-model-subtitle">Planning & Analysis</p>
                  </div>
                  <div className="ai-innovation-model-column">
                    <h4>E2E AI Package</h4>
                    <p className="ai-innovation-model-subtitle">End-to-End Solution</p>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Discover challenges and opportunities</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Study current state of awareness</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Define goals along with business</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Assess data readiness</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Validate biases in data</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Identify business workflows to be eliminated/automated</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Choose tools, technology and models</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Propose solutions</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Experiment or prototype</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Implement solutions</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Test changes</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Take changes to Live</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>

                <div className="ai-innovation-models-row">
                  <div className="ai-innovation-row-header">Maintain & improvise</div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-no">✗</span>
                  </div>
                  <div className="ai-innovation-model-content">
                    <span className="ai-innovation-yes">✓</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Managed AI Labs</h2>
              <div className="playbooks-grid">
                <div className="playbook-card">
                  <h4>Incubation Center</h4>
                  <p>Parternship with technology institutes.</p>
                  <p>Dedicated spaces for AI experimentation.</p>
                </div>
                <div className="playbook-card">
                  <h4>Off Roadmap</h4>
                  <p>Rapid prototyping and quick validation of AI ideas.</p>
                  <p>Tools, Technology & Model evaluation support.</p>

                </div>
                <div className="playbook-card">
                  <h4>Cost conscious</h4>
                  <p>Internship hiring and onboarding for experiments.</p>
                  <p>Shared AI CoE resources, licenses and infrastructure. </p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Strategic Engagement</h2>
              <div className="playbooks-grid">
                <div className="playbook-card">
                  <h4>Analyze</h4>
                  <p>AI Awareness: Study how much of your organization is aware of AI technology & application.</p>
                  <p>Bias Detection: Define training data set requirements that eliminate bias and ensure fairness.</p>
                  <p>Data Strategy: Comprehensive data governance and quality management for AI success</p>
                </div>
                <div className="playbook-card">
                  <h4>Propose</h4>
                  <p>Analyze the business workflows and touch-points that requires automation.</p>
                  <p>Evaluate the right tools and models for the proposed use cases.</p>
                  <p>Organizational change strategies for successful AI adoption</p>
                  <p>Recommend mitigation plan for the risks identified.</p>
                </div>
              </div>
            </div>

            <div className="content-section">
              <h2>Complete AI Package</h2>
              <div className="ai-circular-workflow">
                {/* Dashed circle outline */}
                <div className="ai-workflow-circle-outline"></div>

                {/* Stage cards positioned at 90° intervals on the circle line */}
                <div className="ai-workflow-stage-card ai-workflow-assess">
                  <div className="ai-workflow-stage-header">
                    <div className="ai-workflow-stage-icon">
                      <Target size={24} strokeWidth={2} />
                    </div>
                    <h4 className="ai-workflow-stage-title">Assess</h4>
                  </div>
                  <ul className="ai-workflow-stage-list">
                    <li>Study AI awareness across organization</li>
                    <li>Evaluate data maturity & infrastructure readiness</li>
                  </ul>
                </div>

                <div className="ai-workflow-stage-card ai-workflow-identify">
                  <div className="ai-workflow-stage-header">
                    <div className="ai-workflow-stage-icon">
                      <BarChart3 size={24} strokeWidth={2} />
                    </div>
                    <h4 className="ai-workflow-stage-title">Identify</h4>
                  </div>
                  <ul className="ai-workflow-stage-list">
                    <li>Identify high-impact AI applications</li>
                    <li>Choose optimal platforms & frameworks</li>
                  </ul>
                </div>

                <div className="ai-workflow-stage-card ai-workflow-implement">
                  <div className="ai-workflow-stage-header">
                    <div className="ai-workflow-stage-icon">
                      <TrendingUp size={24} strokeWidth={2} />
                    </div>
                    <h4 className="ai-workflow-stage-title">Implement</h4>
                  </div>
                  <ul className="ai-workflow-stage-list">
                    <li>Train employees on chosen technologies</li>
                    <li>Phased approach with clear milestones</li>
                    <li>Implement workflow changes</li>
                  </ul>
                </div>

                <div className="ai-workflow-stage-card ai-workflow-measure">
                  <div className="ai-workflow-stage-header">
                    <div className="ai-workflow-stage-icon">
                      <Award size={24} strokeWidth={2} />
                    </div>
                    <h4 className="ai-workflow-stage-title">Measure</h4>
                  </div>
                  <ul className="ai-workflow-stage-list">
                    <li>Financial modeling & business case development</li>
                    <li>Measure AI adoption impact</li>
                  </ul>
                </div>


              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AIInnovation;
